import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { noOp } from '../../lib/utils';
import { Icon } from '../Icon';

export type ValidatedStringMessageProps = {
  /**
   * Maximum length for input
   */
  maxLength: number;
  /**
   * Value length of typed input
   */
  valueLength: number;
  /**
   * Get validation result for the Input
   */
  onValidate?(isValid: boolean): void;
};

const isValidated: (valueLength: number, maxLength: number) => boolean = (
  valueLength,
  maxLength,
) => {
  return valueLength / maxLength <= 1.0;
};

const isTooShortToShow: (valueLength: number, maxLength: number) => boolean = (
  valueLength,
  maxLength,
) => {
  return valueLength / maxLength < 0.5;
};

export const ValidatedStringMessage: FC<ValidatedStringMessageProps> = ({
  maxLength = Infinity,
  valueLength,
  onValidate = noOp,
}: ValidatedStringMessageProps) => {
  const { t } = useTranslation();

  const isValid = isValidated(valueLength, maxLength);
  const isShorterThanMax = isTooShortToShow(valueLength, maxLength);

  useEffect(() => onValidate(isValid), [isValid, onValidate]);

  return (
    <div
      className={`text-2xs flex flex-col-reverse sm:flex-row sm:space-x-1 items-end sm:items-center justify-between relative top-1 ${
        !isValid && 'text-red-200'
      }`}
    >
      <div className="flex items-center w-full space-x-1 sm:w-9/12">
        {!isValid && (
          <>
            <div className="w-4 h-4 text-red-200 fill-current">
              <Icon name="Error" />
            </div>
            <p data-testid="exceed-character-error" className="lg:h-2/3">
              {t('errors:textLimit')}
            </p>
          </>
        )}
      </div>
      <div className="flex items-center ml-4 text-right">
        {!isShorterThanMax && (
          <p data-testid="character-count" className="lg:h-2/3">
            {valueLength} / {maxLength}
          </p>
        )}
      </div>
    </div>
  );
};
